import logger from '@/lib/logger';
import { useState, useRef, useEffect } from 'react';
import { useDeviceStore } from '@/store';
import { deviceService } from '@/services/device';
import {
  encodeAppMessage,
  decodeDevMessage,
  decodeDevMessageRaw,
  createBlinkMessage,
  createStatusMessage,
  createMeasurementMessage,
  TIA,
  ADC,
  CHAN,
  Reference,
  AppMessage,
} from '@/lib/protobuf';
import type { DevMessage } from '@/lib/protobuf';

interface ReceivedMessage {
  id: number;
  timestamp: Date;
  data: Uint8Array;
  decoded?: DevMessage | AppMessage;
  messageType?: 'DevMessage' | 'AppMessage';
  source?: 'watchdog' | 'response' | 'file' | 'sent';
  error?: string;
}

const TEST_MESSAGES = {
  blink: {
    name: 'LED Blink',
    description: 'Trigger device LED blink',
    create: () => createBlinkMessage(),
  },
  status: {
    name: 'Status Request',
    description: 'Query device status',
    create: () => createStatusMessage(),
  },
  simpleMeasurement: {
    name: 'Simple Measurement (1kHz)',
    description: '100mV, 1000Hz, CH1',
    create: () =>
      createMeasurementMessage({
        amplitudeMV: [100],
        rtiaOhm: TIA.Resistor_10kOhm,
        biasMV: [0],
        adcGain: ADC.Gain_1,
        channels: [CHAN.CH1],
        frequencyHz: [1000],
        // Placeholder defaults pending firmware/domain sign-off (Story 1.6) — not tuned values.
        // periodsMin/durationMinMs use their proto minimum of 1 (0 is out of range for these two).
        intervalMs: [],
        settlePeriods: 0,
        settleMs: 0,
        periodsMin: 1,
        durationMinMs: 1,
        frequencySnapPct: 0,
        reference: Reference.BEFORE,
        hold: undefined,
      }),
  },
} as const;

export function DeviceTestPage() {
  const { connectionState, deviceInfo } = useDeviceStore();
  const [selectedMessage, setSelectedMessage] = useState<keyof typeof TEST_MESSAGES>('blink');
  const [receivedMessages, setReceivedMessages] = useState<ReceivedMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [messageIdCounter, setMessageIdCounter] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isConnected = connectionState === 'connected';

  // Subscribe to unsolicited (watchdog) messages when connected
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = deviceService.onUnsolicitedMessage((msg, raw) => {
      setReceivedMessages((prev) => [
        {
          id: Date.now(),
          timestamp: new Date(),
          data: raw,
          decoded: msg,
          messageType: 'DevMessage',
          source: 'watchdog',
        },
        ...prev,
      ]);
    });

    return unsubscribe;
  }, [isConnected]);

  const handleConnect = async () => {
    try {
      await deviceService.connect();
    } catch (error) {
      logger.error('[DeviceTest] Connection failed:', error);
    }
  };

  const handleDisconnect = async () => {
    try {
      await deviceService.disconnect();
    } catch (error) {
      logger.error('[DeviceTest] Disconnect failed:', error);
    }
  };

  const handleSend = async () => {
    if (!isConnected || isSending) return;

    setIsSending(true);
    try {
      const messageConfig = TEST_MESSAGES[selectedMessage];
      const message = messageConfig.create();
      const encoded = encodeAppMessage(message);

      logger.trace('[DeviceTest] Sending:', selectedMessage, encoded.length, 'bytes');
      logger.trace('[DeviceTest] Full frame (hex):', Array.from(encoded).map((b: number) => b.toString(16).padStart(2, '0').toUpperCase()).join(' '));

      // Record the sent AppMessage
      const sentId = Date.now();
      setReceivedMessages(prev => [
        {
          id: sentId,
          timestamp: new Date(),
          data: encoded,
          decoded: message,
          messageType: 'AppMessage',
          source: 'sent',
        },
        ...prev,
      ]);
      setMessageIdCounter(prev => prev + 1);

      // Send message and capture response
      let response: DevMessage | null = null;
      
      if (selectedMessage === 'blink') {
        response = await deviceService.blink();
      } else if (selectedMessage === 'status') {
        response = await deviceService.getState();
      } else if (selectedMessage === 'simpleMeasurement') {
        // For measurement, send MEASURE_START trigger with the impedanceExec config
        const measureConfig = messageConfig.create();
        response = await deviceService.sendCustomCommand(
          measureConfig.trig,
          measureConfig.impedanceExec
        );
      }
      
      if (response) {
        logger.trace('[DeviceTest] Received response:', response);

        // Capture the raw bytes from the service (set during the receive loop)
        const rawResponse = deviceService.getLastRawResponse() ?? new Uint8Array();
        
        // Add to received messages list
        setReceivedMessages(prev => [
          {
            id: messageIdCounter,
            timestamp: new Date(),
            data: rawResponse,
            decoded: response,
            messageType: 'DevMessage',
            source: 'response',
          },
          ...prev,
        ]);
        setMessageIdCounter(prev => prev + 1);
      } else {
        logger.warn('[DeviceTest] No response received from device');
      }
    } catch (error) {
      logger.error('[DeviceTest] Send failed:', error);
      if (error instanceof Error) {
        logger.error('[DeviceTest] Error details:', {
          message: error.message,
          stack: error.stack,
          ...(error as any),
        });
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);
      
      logger.trace('[DeviceTest] ========================================');
      logger.trace('[DeviceTest] File Upload Decode Attempt');
      logger.trace('[DeviceTest] ========================================');
      logger.trace('[DeviceTest] File:', file.name);
      logger.trace('[DeviceTest] Size:', data.length, 'bytes');
      logger.trace('[DeviceTest] First 32 bytes:', Array.from(data.slice(0, 32)).map(b => '0x' + b.toString(16).padStart(2, '0')).join(' '));
      
      let decoded: DevMessage | AppMessage | undefined;
      let messageType: 'DevMessage' | 'AppMessage' | undefined;
      let error: string | undefined;
      let decodeMethod = '';

      // Try framed DevMessage format first (Device → App messages)
      logger.trace('[DeviceTest] [1] Trying framed DevMessage format (0xAA 0x55 + size + protobuf + CRC32)...');
      try {
        decoded = decodeDevMessage(data);
        messageType = 'DevMessage';
        decodeMethod = 'Framed DevMessage with CRC32';
        logger.trace('[DeviceTest] \u2713 SUCCESS - Decoded as framed DevMessage');
        logger.trace('[DeviceTest] Result:', decoded);
      } catch (framedErr) {
        logger.trace('[DeviceTest] \u2717 FAILED - Framed decode error:', framedErr);
        
        // Try raw DevMessage protobuf format
        logger.trace('[DeviceTest] [2] Trying raw DevMessage protobuf format (no framing)...');
        try {
          decoded = decodeDevMessageRaw(data);
          messageType = 'DevMessage';
          decodeMethod = 'Raw DevMessage protobuf (no framing)';
          logger.trace('[DeviceTest] \u2713 SUCCESS - Decoded as raw DevMessage protobuf');
          logger.trace('[DeviceTest] Result:', decoded);
        } catch (rawErr) {
          logger.trace('[DeviceTest] \u2717 FAILED - Raw DevMessage decode error:', rawErr);
          
          // Try raw AppMessage protobuf format (App → Device messages)
          logger.trace('[DeviceTest] [3] Trying raw AppMessage protobuf format...');
          try {
            decoded = AppMessage.decode(data);
            messageType = 'AppMessage';
            decodeMethod = 'Raw AppMessage protobuf (App → Device)';
            logger.trace('[DeviceTest] \u2713 SUCCESS - Decoded as raw AppMessage protobuf');
            logger.trace('[DeviceTest] Result:', decoded);
          } catch (appErr) {
            logger.trace('[DeviceTest] \u2717 FAILED - Raw AppMessage decode error:', appErr);
            error = `All formats failed:\n\n` +
                    `[Framed DevMessage] ${framedErr instanceof Error ? framedErr.message : String(framedErr)}\n\n` +
                    `[Raw DevMessage] ${rawErr instanceof Error ? rawErr.message : String(rawErr)}\n\n` +
                    `[Raw AppMessage] ${appErr instanceof Error ? appErr.message : String(appErr)}`;
            logger.error('[DeviceTest] All decode attempts failed');
          }
        }
      }
      
      logger.trace('[DeviceTest] ========================================');

      setReceivedMessages(prev => [
        {
          id: messageIdCounter,
          timestamp: new Date(),
          data,
          decoded,
          messageType,
          error: error || (decoded ? `✓ ${decodeMethod}` : undefined),
        },
        ...prev,
      ]);
      setMessageIdCounter(prev => prev + 1);
    } catch (err) {
      logger.error('[DeviceTest] File read failed:', err);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle received messages
  // TODO: Subscribe to device messages when DeviceService implements it
  // useEffect(() => {
  //   const handleMessage = (data: Uint8Array) => {
  //     let decoded: DevMessage | undefined;
  //     let error: string | undefined;

  //     try {
  //       decoded = decodeDevMessage(data);
  //     } catch (err) {
  //       error = err instanceof Error ? err.message : 'Unknown error';
  //     }

  //     setReceivedMessages(prev => [
  //       {
  //         id: messageIdCounter,
  //         timestamp: new Date(),
  //         data,
  //         decoded,
  //         error,
  //       },
  //       ...prev,
  //     ]);
  //     setMessageIdCounter(prev => prev + 1);
  //   };

  //   deviceService.on('message', handleMessage);
  //   return () => deviceService.off('message', handleMessage);
  // }, [messageIdCounter]);

  const downloadMessage = (msg: ReceivedMessage) => {
    const blob = new Blob([new Uint8Array(msg.data)], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `device-message-${msg.id}.bin`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const deleteMessage = (id: number) => {
    setReceivedMessages(prev => prev.filter(msg => msg.id !== id));
  };

  const clearAllMessages = () => {
    setReceivedMessages([]);
  };

  const formatBytes = (bytes: Uint8Array): string => {
    return Array.from(bytes).map(b => '0x' + b.toString(16).padStart(2, '0').toUpperCase()).join(' ');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Warning Banner */}
      <div className="bg-yellow-100 dark:bg-yellow-900 border-l-4 border-yellow-500 p-4 mb-6">
        <p className="text-sm text-yellow-700 dark:text-yellow-400 font-semibold" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg className="flex-shrink-0" style={{ width: '16px', height: '16px' }} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Diagnostic Tool - For testing and debugging device communication only
        </p>
      </div>

      <h1 className="text-3xl font-bold mb-8 text-gray-900 dark:text-gray-100">Device Diagnostic</h1>

      {/* Connection Card */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Connection</h2>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={isConnected ? handleDisconnect : handleConnect}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                isConnected
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isConnected ? 'Disconnect' : 'Connect to Device'}
            </button>
            <div
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isConnected ? 'bg-green-500' : 'bg-gray-300'
              } text-white`}
            />
            <span className="text-gray-700 dark:text-gray-300">
              {connectionState === 'connected' && deviceInfo
                ? `Connected: ${deviceInfo.firmwareVersion}`
                : connectionState}
            </span>
          </div>
        </div>
      </div>

      {/* Send Message Card */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Send Message</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="test-message-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Test Message
            </label>
            <select
              id="test-message-select"
              value={selectedMessage}
              onChange={(e) => setSelectedMessage(e.target.value as keyof typeof TEST_MESSAGES)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
            >
              {Object.entries(TEST_MESSAGES).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.name} - {config.description}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleSend}
            disabled={!isConnected || isSending}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors"
          >
            {isSending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>

      {/* Upload Binary File Card */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Decode Binary File</h2>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Upload a .bin file to decode. The tool automatically detects:
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside ml-2">
            <li><strong>Framed DevMessage:</strong> [0xAA][0x55][Size][Protobuf][CRC32] (Device → App)</li>
            <li><strong>Raw DevMessage:</strong> Protobuf without framing (Device → App)</li>
            <li><strong>Raw AppMessage:</strong> Protobuf without framing (App → Device)</li>
          </ul>
          <input
            ref={fileInputRef}
            type="file"
            accept=".bin"
            onChange={handleFileUpload}
            className="hidden"
            aria-label="Upload binary file"
          />
          <button
            onClick={handleUploadClick}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-medium transition-colors"
          >
            Upload Binary File
          </button>
        </div>
      </div>

      {/* Received Messages Card */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Messages ({receivedMessages.length})
          </h2>
          {receivedMessages.length > 0 && (
            <button
              onClick={clearAllMessages}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md font-medium transition-colors"
            >
              Clear All
            </button>
          )}
        </div>
        <div className="space-y-4">
          {receivedMessages.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No messages received yet
            </p>
          ) : (
            receivedMessages.map((msg) => (
              <div
                key={msg.id}
                className="border border-gray-300 dark:border-gray-600 rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Message #{msg.id}
                    </span>
                    {msg.source === 'watchdog' && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 border border-orange-400">
                        WATCHDOG
                      </span>
                    )}
                    {msg.source === 'sent' && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border border-blue-400">
                        SENT →
                      </span>
                    )}
                    {msg.source === 'response' && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border border-green-400">
                        ← RESPONSE
                      </span>
                    )}
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                      {msg.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => downloadMessage(msg)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => deleteMessage(msg.id)}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="mt-2">
                  {msg.data.length > 0 && (
                    <p className="text-xs font-mono text-gray-600 dark:text-gray-400">
                      <span className="font-semibold">Size:</span> {msg.data.length} bytes
                    </p>
                  )}
                  {msg.data.length > 0 && (
                    <p className="text-xs font-mono text-gray-600 dark:text-gray-400 break-all">
                      <span className="font-semibold">Hex:</span> {formatBytes(msg.data)}
                    </p>
                  )}
                  {msg.error && (
                    <p className={`text-xs mt-1 ${msg.error.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      <span className="font-semibold">{msg.error.startsWith('✓') ? 'Format:' : 'Decode Error:'}</span> {msg.error}
                    </p>
                  )}
                  {msg.decoded && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      <span className="font-semibold">
                        {msg.messageType === 'AppMessage' ? 'AppMessage (App → Device):' : 'DevMessage (Device → App):'}
                      </span>
                      <pre className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded overflow-x-auto">
                        {JSON.stringify(msg.decoded, (_key, value) => {
                          // Convert bigint to string for display
                          if (typeof value === 'bigint') {
                            return value.toString() + 'n';
                          }
                          // Convert Uint8Array to hex string
                          if (value instanceof Uint8Array) {
                            return Array.from(value).map(b => b.toString(16).padStart(2, '0')).join(':');
                          }
                          return value;
                        }, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
